// src/services/activityReportService.js

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import activityReportApi from '@/apis/activityReport';
import { formatTableDate } from '@/utils/dateFormatter';
import { parseFlexibleDecimal } from '@/utils/parseFlexibleDecimal';
import { normalizeTimesheetsForApi } from '@/pages/ActivityReport/utils/normalizeTimesheetsForApi';
import { sanitizeCraOwnInvoiceVatFields } from '@/lib/activity-report/craVatSelection';
import { normalizeExpenseVatRateForApi } from '@/lib/activity-report/normalizeExpenseVatRate';
import { getActivityReportMissionEndYmd } from '@activity-report-mission-period';

const simulateDelay = (ms = 400) => new Promise((resolve) => setTimeout(resolve, ms));

const formatFullName = (firstName, lastName) => {
  const parts = [];
  if (firstName) parts.push(firstName);
  if (lastName) parts.push(lastName);
  return parts.join(' ') || '-';
};

const formatPersonDisplayName = (person) => {
  if (!person) return '';
  if (typeof person === 'string') return person;
  const firstName = person.first_name ?? person.firstName ?? '';
  const lastName = person.last_name ?? person.lastName ?? '';
  const full = [firstName, lastName].filter(Boolean).join(' ');
  return full || person.email || '';
};

/** Supabase renvoie souvent `referents` comme tableau — ne pas le passer tel quel à formatPersonDisplayName. */
const resolveReferentEntity = (value) => {
  if (!value) return null;
  if (Array.isArray(value)) return value[0] ?? null;
  return value;
};

const resolveMissionReferentEntity = (mission) => {
  if (!mission || typeof mission !== 'object') return null;
  return resolveReferentEntity(mission.referents ?? mission.referent ?? null);
};

const normalizeMissionPricingType = (missionType) => {
  const map = {
    hourly: 'hourly',
    daily: 'daily',
    monthly: 'monthly',
    deliverable: 'deliverable',
  };

  if (!missionType) return '';
  const lower = String(missionType).toLowerCase();
  return map[lower] || lower;
};

/** @returns {number | null} Percent in (0, 100] for draft/submit payload */
function mapExpenseReimbursementPercentForApi(exp) {
  const raw = exp.reimbursementPercent ?? exp.reimbursement_percent;
  if (raw === undefined || raw === null || raw === '') return null;
  const numeric = parseFloat(String(raw));
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return Math.min(100, Math.round(numeric * 100) / 100);
}

const inferReportType = (item) => {
  const idStr = String(item.id || '');
  const suffix = idStr.includes('-') ? idStr.split('-').pop() : '';
  if (suffix === 'expenses') return 'expense';
  return 'invoice';
};

const mapSupplementaryItemFromApi = (item) => {
  if (!item) {
    return { description: '', amount: 0, justificationFile: null };
  }
  const rawFile = item.justificationFile || item.justification_file;
  const key =
    item.justification_key ||
    rawFile?.key ||
    rawFile?.file_key ||
    null;
  const url =
    item.justification_url ||
    rawFile?.url ||
    rawFile?.file_url ||
    null;
  const name =
    item.justification_file_name ||
    rawFile?.name ||
    rawFile?.file_name ||
    null;
  const justificationFile =
    key || url
      ? {
          key: key || null,
          url: url || null,
          name: name || null,
        }
      : null;
  return {
    id: item.id || null,
    orderIndex: item.order_index ?? item.orderIndex ?? null,
    description: item.description || '',
    amount: item.amount_excl_vat ?? item.amount ?? 0,
    missionId: item.mission_id ?? item.missionId ?? null,
    mission_id: item.mission_id ?? item.missionId ?? null,
    projectCodeId: item.project_code_id ?? item.projectCodeId ?? null,
    justificationFile,
  };
};

const normalizeSupplementaryItemsForApi = (items) => {
  if (!Array.isArray(items)) return [];
  return items.map((item) => {
    const justificationFile = item.justificationFile || item.justification_file;
    return {
      description: item.description || '',
      amount: item.amount ?? item.amount_excl_vat ?? 0,
      mission_id: item.missionId ?? item.mission_id ?? null,
      project_code_id: item.projectCodeId ?? item.project_code_id ?? null,
      justification_key:
        justificationFile?.key ?? justificationFile?.file_key ?? item.justification_key ?? null,
      justification_url:
        justificationFile?.url ?? justificationFile?.file_url ?? item.justification_url ?? null,
      justification_file_name:
        justificationFile?.name ??
        justificationFile?.file_name ??
        item.justification_file_name ??
        null,
    };
  });
};

const extractOwnInvoiceFileUrl = (options = {}) => {
  const ownInvoice = options.own_invoice || options.ownInvoice;
  if (!ownInvoice || typeof ownInvoice !== 'object') return null;
  return ownInvoice.file_url || ownInvoice.fileUrl || ownInvoice.url || ownInvoice.invoice_file?.file_url || ownInvoice.invoice_file?.url || null;
};

const normalizeReport = (item) => {
  const type = inferReportType(item);
  const paid = type === 'expense' ? !!item.expenses_is_paid : !!item.invoicing_is_paid;
  const ownInvoiceFileUrl = extractOwnInvoiceFileUrl(item.invoicing_options || {});
  const missionNames = Array.isArray(item.mission_names)
    ? item.mission_names
    : Array.isArray(item.missions)
      ? item.missions.map((mission) => mission?.name ?? mission?.mission_name).filter(Boolean)
      : item.mission_name
        ? [item.mission_name]
        : [];
  return {
    id: item.id,
    missionId: item.mission_id,
    mission: missionNames.join(', '),
    missionCount: missionNames.length || 1,
    missions: Array.isArray(item.missions) ? item.missions : [],
    month: item.report_month,
    freelancer: formatFullName(item.freelancer_first_name, item.freelancer_last_name),
    freelancerData: {
      first_name: item.freelancer_first_name || '',
      last_name: item.freelancer_last_name || '',
      avatar: item.freelancer_avatar || null,
      legal_entity_type: item.freelancer_legal_entity_type || null,
      portage_company_name: item.freelancer_portage_company_name || null,
    },
    referent: formatFullName(item.freelancer_referent_first_name, item.freelancer_referent_last_name) || '-',
    referentId: item.freelancer_referent_id || null,
    hasExternalReferent: !!item.has_external_referent,
    submissionDate: item.submission_date ? formatTableDate(item.submission_date) : '-',
    status: item.status ? item.status.toLowerCase() : 'draft',
    isNilReport: !!(item.is_nil_report ?? item.isNilReport),
    rejectionReason: item.rejection_reason || item.rejectionReason || null,
    type,
    paid,
    invoicingPaid: !!item.invoicing_is_paid,
    expensesPaid: !!item.expenses_is_paid,
    invoicingEnabled: !!item.invoicing_enabled,
    expensesEnabled: !!item.expenses_enabled,
    sentToAccounting: !!item.sent_to_accounting,
    documentUrl: item.document_url || null,
    ownInvoiceFileUrl,
    paymentFileUrl: item.payment_file_url || null,
    invoiceAmountHT: item.invoice_amount_ht || item.invoice_amount_ht_excl_vat || 0,
    expenseAmountHT: item.expense_amount_ht || item.expense_amount_ht_excl_vat || 0,
    invoice_amount_ht: item.invoice_amount_ht || item.invoice_amount_ht_excl_vat || 0,
    expense_amount_ht: item.expense_amount_ht || item.expense_amount_ht_excl_vat || 0,
    currency: item.currency || 'EUR',
  };
};

dayjs.extend(utc);

const normalizeInvoiceFile = (file = {}) => {
  if (!file) return null;
  const name = file.name || file.file_name || '';
  const url = file.url || file.file_url || '';
  const key = file.key || file.file_key || '';
  if (!name && !url && !key) return null;
  return { name, url, key };
};

const normalizeInvoiceGenerator = (payload = {}) => ({
  purchaseOrderNumber: payload.purchase_order_number || payload.purchaseOrderNumber || '',
  invoiceNumber: payload.invoice_number || payload.invoiceNumber || '',
  eligibleToVat: payload.eligible_to_vat ?? payload.vat_eligibility ?? payload.eligibleToVat ?? false,
  vatNumber: payload.vat_number || payload.vatNumber || '',
});

const normalizeOwnInvoice = (payload = {}) => {
  // Chercher le fichier dans plusieurs emplacements possibles
  const fileSource =
    payload.file || // Structure migrée: own_invoice.file
    payload.invoice_file || // Ancienne structure
    (payload.file_name || payload.file_url || payload.file_key ? payload : null); // Propriétés directes

  const ownInvoice = {
    amountExclVAT: payload.amount_excl_vat || payload.amount_excluding_vat || payload.amountExclVAT || '',
    amountInclVAT: payload.amount_incl_vat || payload.amount_including_vat || payload.amountInclVAT || '',
    subjectToVat:
      payload.subject_to_vat ?? payload.subjectToVat ?? undefined,
    vatRate: payload.vat_rate ?? payload.vatRate ?? null,
    vatSelection: payload.vat_selection ?? payload.vatSelection ?? null,
    vatRegime: payload.vat_regime ?? payload.vatRegime ?? null,
    invoiceNumber: payload.invoice_number || payload.invoiceNumber || '',
    file: normalizeInvoiceFile(fileSource),
  };

  return sanitizeCraOwnInvoiceVatFields(ownInvoice);
};

const normalizeInvoicingOptions = (options = {}) => {
  const invoiceType = options.invoice_type || options.invoiceType || options.mode || 'generator';
  const invoiceGenerator = normalizeInvoiceGenerator(options.invoice_generator || options.invoiceGenerator || options);
  const ownInvoice = normalizeOwnInvoice(options.own_invoice || options.ownInvoice || options);

  return {
    invoiceType,
    invoiceGenerator,
    ownInvoice,
  };
};

const toNumberOrNull = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

/** Accepte virgule décimale (ex. FR) pour persistance API / colonnes NUMERIC & INTEGER */
const carbonOneWayDistanceKmToApi = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const n = parseFlexibleDecimal(value);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100) / 100;
};

const carbonTripsPerWeekToApi = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const n = parseFlexibleDecimal(value);
  if (!Number.isFinite(n)) return null;
  return Math.round(n);
};

const buildInvoicingOptionsPayload = (invoicing = {}) => {
  // Aligné sur le store CRA (own_invoice) — évite de perdre vat_selection en brouillon.
  const invoiceType = invoicing.invoiceType || 'own_invoice';
  const isGenerator = invoiceType === 'generator';

  const generator = isGenerator
    ? {
        purchase_order_number: invoicing.invoiceGenerator?.purchaseOrderNumber || '',
        invoice_number: invoicing.invoiceGenerator?.invoiceNumber || '',
        eligible_to_vat: !!invoicing.invoiceGenerator?.eligibleToVat,
        vat_number: invoicing.invoiceGenerator?.vatNumber || '',
      }
    : null;

  const sanitizedOwnInvoice = sanitizeCraOwnInvoiceVatFields(invoicing.ownInvoice ?? {});
  const vatSelection =
    sanitizedOwnInvoice.vatSelection ?? sanitizedOwnInvoice.vat_selection ?? null;

  const own = !isGenerator
    ? {
        amount_excl_vat: toNumberOrNull(sanitizedOwnInvoice.amountExclVAT),
        amount_incl_vat: toNumberOrNull(sanitizedOwnInvoice.amountInclVAT),
        ...(vatSelection
          ? {
              subject_to_vat: !!sanitizedOwnInvoice.subjectToVat,
              vat_rate: toNumberOrNull(sanitizedOwnInvoice.vatRate),
              vat_selection: vatSelection,
              vat_regime:
                sanitizedOwnInvoice.vatRegime ?? sanitizedOwnInvoice.vat_regime ?? null,
            }
          : {
              subject_to_vat: false,
              vat_rate: null,
              vat_selection: null,
              vat_regime: null,
            }),
        invoice_number: sanitizedOwnInvoice.invoiceNumber || '',
      }
    : null;

  if (own && invoicing.ownInvoice?.file) {
    own.file_name = invoicing.ownInvoice.file.name || '';
    own.file_key = invoicing.ownInvoice.file.key || '';
    own.file_url = invoicing.ownInvoice.file.url || '';
  }

  return {
    invoice_type: invoiceType,
    invoice_generator: generator,
    own_invoice: own,
  };
};

const mapTimesheetProjectCodeFromApi = (ts) => {
  const projectCodeId = ts.project_code_id ?? ts.projectCodeId ?? null;
  return projectCodeId != null && projectCodeId !== ''
    ? { projectCodeId, project_code_id: projectCodeId }
    : {};
};

const mapFinancialSummary = (payload = {}) => ({
  invoiceAmountExclVAT: toNumberOrNull(payload.invoice_amount_excl_vat ?? payload.invoiceAmountExclVAT),
  expensesAmountExclVAT: toNumberOrNull(payload.expenses_amount_excl_vat ?? payload.expensesAmountExclVAT),
  expensesVatTotal: toNumberOrNull(payload.expenses_vat_total ?? payload.expensesVatTotal),
});

const normalizeAddressLines = (info = {}) => {
  if (!info) return [];
  const lines = [];
  if (info.address) lines.push(info.address);
  const cityLine = [info.postal_code || info.postalCode, info.city].filter(Boolean).join(' ').trim();
  if (cityLine) lines.push(cityLine);
  if (info.country) lines.push(info.country);
  return lines;
};

const normalizeFreelancerBankInfo = (info = {}) => {
  if (!info || typeof info !== 'object') return null;
  return {
    name: info.name || info.company_name || info.freelancer_name || '',
    addressLines: normalizeAddressLines(info),
    address: info.address || '',
    postalCode: info.postal_code || info.postalCode || '',
    city: info.city || '',
    country: info.country || '',
    sirenNumber: info.siren_number || info.sirenNumber || '',
    vatNumber: info.vat_number || info.vatNumber || '',
    iban: info.iban || '',
    bic: info.bic || '',
  };
};

const normalizeCompanyInfo = (info = {}) => {
  if (!info || typeof info !== 'object') return null;
  return {
    name: info.name || info.company_name || '',
    addressLines: normalizeAddressLines(info),
    address: info.address || '',
    postalCode: info.postal_code || info.postalCode || '',
    city: info.city || '',
    country: info.country || '',
    siren: info.siren_number || info.sirenNumber || '',
    vatNumber: info.vat_number || info.vatNumber || '',
    iban: info.iban || '',
  };
};

const normalizeReportExport = (payload = {}) => {
  if (!payload) return null;
  return {
    id: payload.id,
    reportMonth: payload.report_month || payload.reportMonth || '',
    status: payload.status ? payload.status.toLowerCase() : null,
    submissionDate: payload.submission_date || payload.submissionDate || null,
    mission: payload.mission || null,
    missionDescription: payload.mission?.description || payload.mission_description || '',
    freelancerBankInfo: normalizeFreelancerBankInfo(payload.freelancer_bank_info),
    companyInfo: normalizeCompanyInfo(payload.company_info),
    timesheets: Array.isArray(payload.timesheets) ? payload.timesheets : [],
    expenses: Array.isArray(payload.expenses) ? payload.expenses : [],
    supplementaryItems: Array.isArray(payload.supplementary_items)
      ? payload.supplementary_items.map(mapSupplementaryItemFromApi)
      : [],
    invoicing: normalizeInvoicingOptions(payload.invoicing_options || payload.invoicing || {}),
    // Ajouter les invoices migrées (accounting_entries)
    invoices: Array.isArray(payload.invoices) ? payload.invoices : [],
    accounting_entries: Array.isArray(payload.accounting_entries) ? payload.accounting_entries : [],
    financialSummary: mapFinancialSummary(payload),
  };
};

const normalizeMissionRowFromDetail = (missionRow) => {
  if (!missionRow || typeof missionRow !== 'object') return missionRow;
  const operationalEnd =
    getActivityReportMissionEndYmd(missionRow) ?? missionRow.end_date ?? null;
  return {
    ...missionRow,
    id: missionRow.id ?? missionRow.mission_id,
    name: missionRow.name || '',
    missionType: missionRow.mission_type || missionRow.missionType || '',
    pricingType: normalizeMissionPricingType(
      missionRow.mission_type || missionRow.missionType || missionRow.pricingType
    ),
    fee: missionRow.rate_amount ?? missionRow.fee ?? null,
    rate_amount: missionRow.rate_amount ?? missionRow.fee ?? null,
    currency: missionRow.currency || 'EUR',
    endDate: operationalEnd,
    operationalEndDate: operationalEnd,
    project_codes: Array.isArray(missionRow.project_codes) ? missionRow.project_codes : [],
    projectCodes: Array.isArray(missionRow.project_codes)
      ? missionRow.project_codes
      : missionRow.projectCodes || [],
  };
};

const normalizeReportDetail = (payload) => {
  if (!payload) {
    console.error('normalizeReportDetail: payload is null or undefined');
    return null;
  }
  
  // Handle both 'mission' (singular) and 'missions' (plural from Supabase relation)
  const missionsRaw = Array.isArray(payload.missions)
    ? payload.missions
    : payload.mission
      ? [payload.mission]
      : [];
  const missionsNormalized = missionsRaw.map(normalizeMissionRowFromDetail);
  const mission = missionsNormalized[0] || {};
  const freelancersRaw = payload.freelancer || payload.freelancers;
  const freelancer = Array.isArray(freelancersRaw) ? freelancersRaw[0] : freelancersRaw || {};
  const missionFeedback = payload.mission_feedback ?? '';
  // Handle both 'reviewer' (singular) and 'reviewers' (plural from Supabase relation)
  const reviewerInfo = payload.reviewer || payload.reviewers || null;
  const externalReviewerRaw = payload.external_reviewer || payload.external_reviewers || null;
  const externalReviewerInfo = Array.isArray(externalReviewerRaw)
    ? externalReviewerRaw[0]
    : externalReviewerRaw;
  const reviewedByExternal = !!externalReviewerInfo && !reviewerInfo;
  const reviewerName = reviewedByExternal
    ? formatPersonDisplayName(externalReviewerInfo)
    : formatPersonDisplayName(reviewerInfo);
  const missionReferentEntity = resolveMissionReferentEntity(mission);
  const referentValue = missionReferentEntity || payload.reviewer || payload.referent || null;
  const referentName = formatPersonDisplayName(referentValue);
  const missionReferentObj =
    missionReferentEntity && typeof missionReferentEntity === 'object' ? missionReferentEntity : null;
  const freelancerReferentValue = payload.freelancer_referent || null;
  const freelancerReferentName = formatPersonDisplayName(freelancerReferentValue) || (typeof freelancerReferentValue === 'string' ? freelancerReferentValue : '');
  const displayReferentName = freelancerReferentName || referentName || null;
  
  const normalizedReportMonth = payload.report_month || null
  const missionOperationalEnd =
    getActivityReportMissionEndYmd(mission) ?? mission.end_date ?? null
  
  return {
    id: payload.id,
    status: payload.status ? payload.status.toLowerCase() : null,
    submissionDate: payload.submission_date || null,
    reportMonth: normalizedReportMonth,
    missions: missionsNormalized,
    hasExternalReferent: !!mission.external_referent_contact_id,
    reviewer: reviewerInfo,
    reviewedAt: payload.reviewed_at || null,
    rejectionReason: payload.rejection_reason || null,
    freelancer: {
      id: freelancer.id,
      firstName: freelancer.first_name,
      lastName: freelancer.last_name,
      name: formatFullName(freelancer.first_name, freelancer.last_name),
      email: freelancer.email,
      phone: freelancer.phone,
      teamTag: freelancer.team_tag || null,
      avatar: freelancer.avatar || null,
      referentName: displayReferentName,
      referentFirstName:
        (freelancerReferentValue && typeof freelancerReferentValue === 'object'
          ? freelancerReferentValue.first_name
          : null) ??
        missionReferentObj?.first_name ??
        null,
      referentLastName:
        (freelancerReferentValue && typeof freelancerReferentValue === 'object'
          ? freelancerReferentValue.last_name
          : null) ??
        missionReferentObj?.last_name ??
        null,
      referentAvatar:
        (freelancerReferentValue && typeof freelancerReferentValue === 'object'
          ? freelancerReferentValue.avatar
          : null) ??
        missionReferentObj?.avatar ??
        null,
      referentId:
        freelancer.referent_id || missionReferentObj?.id || mission.referent_id || null,
      legal_entity_type:
        payload.freelancer_legal_entity_type || freelancer.legal_entity_type || null,
      portage_affiliation: payload.portage_affiliation || freelancer.portage_affiliation || null,
    },
    referent: referentName,
    referentInfo: referentValue,
    freelancerReferent: freelancerReferentName,
    freelancerReferentInfo: freelancerReferentValue,
      mission: {
        id: mission.id ?? payload.mission_id,
        name: mission.name || '',
        description: mission.description || '',
        startDate: mission.start_date || null,
        endDate: missionOperationalEnd,
        operationalEndDate: missionOperationalEnd,
        month: payload.report_month || '',
        fee: mission.rate_amount || '',
        currency: mission.currency || 'EUR',
        missionType: mission.mission_type || '',
        pricingType: normalizeMissionPricingType(mission.mission_type),
        referent: referentName,
        referentInfo: missionReferentObj,
        deliverables: mission.deliverables || [], // Include deliverables from mission
        allowSupplementaryItems: mission.allow_supplementary_items ?? false,
        allow_supplementary_items: mission.allow_supplementary_items ?? false,
        requireCarbonFootprint: mission.require_carbon_footprint ?? false,
        require_carbon_footprint: mission.require_carbon_footprint ?? false,
        requireMissionFeedback: mission.require_mission_feedback ?? false,
        require_mission_feedback: mission.require_mission_feedback ?? false,
        allowExpenseReports: mission.allow_expense_reports ?? false,
        allow_expense_reports: mission.allow_expense_reports ?? false,
        expenseCraMode: mission.expense_cra_mode ?? null,
        expense_cra_mode: mission.expense_cra_mode ?? null,
        defaultExpenseReimbursementPercent:
          mission.default_expense_reimbursement_percent ?? 100,
        default_expense_reimbursement_percent:
          mission.default_expense_reimbursement_percent ?? 100,
        requirePeriodEconomicDependency: mission.require_period_economic_dependency ?? false,
        require_period_economic_dependency: mission.require_period_economic_dependency ?? false,
        allowAdminActivityReportReview: mission.allow_admin_activity_report_review === true,
        allow_admin_activity_report_review: mission.allow_admin_activity_report_review === true,
        purchase_order: mission.purchase_order || null,
        external_referent_contact_id: mission.external_referent_contact_id || null,
        max_billable_amount_excl_vat:
          mission.max_billable_amount_excl_vat != null &&
          mission.max_billable_amount_excl_vat !== '' &&
          Number.isFinite(Number(mission.max_billable_amount_excl_vat))
            ? Number(mission.max_billable_amount_excl_vat)
            : null,
        maxBillableAmountExclVat:
          mission.max_billable_amount_excl_vat != null &&
          mission.max_billable_amount_excl_vat !== '' &&
          Number.isFinite(Number(mission.max_billable_amount_excl_vat))
            ? Number(mission.max_billable_amount_excl_vat)
            : null,
        max_total_envelope:
          mission.max_total_envelope != null &&
          mission.max_total_envelope !== '' &&
          Number.isFinite(Number(mission.max_total_envelope))
            ? Number(mission.max_total_envelope)
            : null,
        maxTotalEnvelope:
          mission.max_total_envelope != null &&
          mission.max_total_envelope !== '' &&
          Number.isFinite(Number(mission.max_total_envelope))
            ? Number(mission.max_total_envelope)
            : null,
        project_codes: Array.isArray(mission.project_codes) ? mission.project_codes : [],
        projectCodes: Array.isArray(mission.project_codes) ? mission.project_codes : [],
        // Infos de facturation client renvoyées par le détail (évite un 2e fetch à l'étape facture)
        company_billing_info: mission.company_billing_info || null,
      },
    carbonFootprint: {
      transportationMode: payload.transportation_mode || '',
      oneWayDistance: payload.one_way_distance_km || '',
      tripsPerWeek: payload.trips_per_week || '',
    },
    invoicing: normalizeInvoicingOptions(
      (() => {
        const opts = payload.invoicing_options || {};
        const own = opts.own_invoice || opts.ownInvoice || {};
        // Source de vérité pour le montant : colonne invoice_amount_excl_vat (alignée timesheet + extra)
        if (payload.invoice_amount_excl_vat != null && Number(payload.invoice_amount_excl_vat) >= 0) {
          const ht = Number(payload.invoice_amount_excl_vat);
          // TTC : préférer le montant TTC persisté (own_invoice.amount_incl_vat) s'il existe ;
          // ne le recalculer depuis HT + taux de TVA que s'il est absent (évite une divergence
          // avec le TTC réellement stocké lors de la saisie).
          const persistedIncl = own.amount_incl_vat ?? own.amountInclVAT ?? own.amount_including_vat;
          const persistedInclNum =
            persistedIncl != null && persistedIncl !== '' ? Number(persistedIncl) : null;
          const ttc =
            persistedInclNum != null && Number.isFinite(persistedInclNum)
              ? persistedInclNum
              : (() => {
                  const vatRate = Number(own.vat_rate ?? own.vatRate ?? 0) || 0;
                  return Math.round(ht * (1 + vatRate / 100) * 100) / 100;
                })();
          return {
            ...opts,
            own_invoice: { ...own, amount_excl_vat: ht, amount_incl_vat: ttc },
          };
        }
        return opts;
      })()
    ),
    mission_feedback: missionFeedback,
    period_economic_dependency: payload.period_economic_dependency || null,
    periodEconomicDependency: payload.period_economic_dependency || null,
    reviewSummary: (() => {
      const decision = payload.status ? payload.status.toLowerCase() : null;
      if (
        !payload.reviewed_at ||
        (decision !== 'approved' && decision !== 'rejected')
      ) {
        return null;
      }
      return {
        decision,
        reviewer: reviewerName || null,
        reviewerInfo: reviewedByExternal ? externalReviewerInfo : reviewerInfo,
        reviewedByExternal,
        reviewDate: payload.reviewed_at,
        comments: payload.rejection_reason || null,
      };
    })(),
    timesheets: Array.isArray(payload.timesheets) ? payload.timesheets.map((ts) => {
      // Ensure type field is present (use entryType as fallback)
      const normalizedType = ts.type || ts.entryType || null;
      const typeLc = String(normalizedType || '').toLowerCase();

      // Normalize deliverable timesheets: parse description JSON and add deliverableId (GET detail peut omettre camelCase)
      if (typeLc === 'deliverable' && ts.description) {
        try {
          const parsed = JSON.parse(ts.description);
          const did = parsed.deliverableId ?? parsed.deliverable_id ?? ts.deliverable_id ?? null;
          return {
            ...ts,
            type: 'deliverable',
            deliverableId: did != null && did !== '' ? String(did) : undefined,
            entryType: 'deliverable',
            ...mapTimesheetProjectCodeFromApi(ts),
          };
        } catch {
          // If not JSON, return with type
          return {
            ...ts,
            type: normalizedType || 'deliverable',
            entryType: ts.entryType || 'deliverable',
            ...mapTimesheetProjectCodeFromApi(ts),
          };
        }
      }
      
      // For other types, ensure type is set and date is properly formatted
      // Ensure date is in YYYY-MM-DD format (remove time if present)
      let normalizedDate = ts.date;
      if (normalizedDate && typeof normalizedDate === 'string') {
        // If date includes time, extract only the date part
        if (normalizedDate.includes('T')) {
          normalizedDate = normalizedDate.split('T')[0];
        }
      }
      
      const missionId = ts.mission_id ?? ts.missionId ?? null;
      return {
        ...ts,
        date: normalizedDate,
        type: normalizedType || ts.type || null,
        entryType: ts.entryType || normalizedType || null,
        missionId,
        mission_id: missionId,
        customAmount:
          typeLc === 'monthly' && ts.customAmount == null && ts.amount != null
            ? Number(ts.amount)
            : ts.customAmount,
        ...mapTimesheetProjectCodeFromApi(ts),
      };
    }) : [],
    expenses: (() => {
      const expenses = Array.isArray(payload.expenses) ? payload.expenses : [];
      
      // Map expense types from database format (snake_case) to frontend format (camelCase)
      const mapExpenseTypeToFrontend = (type) => {
        const typeMap = {
          'business_meal': 'businessMeal',
          'transportation': 'transportation',
          'equipment': 'equipment',
          'accommodation': 'accommodation',
          'other': 'other',
        };
        return typeMap[type] || type;
      };
      
      // Normalize expenses: convert receipt_url/receipt_key to receiptFile object
      // Generate a key for each expense if it doesn't have one (using id from DB or timestamp)
      const normalized = expenses.map((exp, index) => {
        const receiptFile = exp.receipt_url || exp.receipt_key ? {
          name: exp.receipt_file_name || 'Receipt',
          url: exp.receipt_url || null,
          key: exp.receipt_key || null,
        } : null;
        return {
          ...exp,
          // Generate key if not present: use id from DB, or create a unique key based on data
          key: exp.key || exp.id || `${exp.date || ''}_${exp.type || ''}_${exp.description || ''}_${exp.amount_excl_vat || exp.amountExclVAT || 0}_${index}_${Date.now()}`,
          // Map type from database format to frontend format
          type: mapExpenseTypeToFrontend(exp.type),
          receiptFile,
          amountInclVAT: exp.amount_incl_vat || exp.amountInclVAT || null,
          amountExclVAT: exp.amount_excl_vat || exp.amountExclVAT || null,
          vatAmount: exp.vat_amount || exp.vatAmount || null,
          vatRate: exp.vat_rate || exp.vatRate || null,
          // Mode de facturation : débours (ancien, note séparée) vs refacturation (intégré facture).
          // Défaut 'debours' à la lecture pour ne pas requalifier les anciennes dépenses.
          billingMode: exp.billing_mode || exp.billingMode || 'refacturation',
          vatRecoverable: (exp.vat_recoverable ?? exp.vatRecoverable) === true,
          reimbursementPercent: exp.reimbursement_percent ?? exp.reimbursementPercent ?? null,
          reimbursement_percent: exp.reimbursement_percent ?? exp.reimbursementPercent ?? null,
          missionId: exp.mission_id ?? exp.missionId ?? null,
          mission_id: exp.mission_id ?? exp.missionId ?? null,
        };
      });
      
      
      return normalized;
    })(),
    supplementaryItems: Array.isArray(payload.supplementary_items)
      ? payload.supplementary_items.map(mapSupplementaryItemFromApi)
      : [],
    financialSummary: mapFinancialSummary(payload),
    invoiceVerificationStatus: payload.invoice_verification_status || null,
    invoiceExtractedAmountExclVat: payload.invoice_extracted_amount_excl_vat != null ? Number(payload.invoice_extracted_amount_excl_vat) : null,
    invoiceExtractedAmountInclVat: payload.invoice_extracted_amount_incl_vat != null ? Number(payload.invoice_extracted_amount_incl_vat) : null,
    invoiceVerifiedAt: payload.invoice_verified_at || null,
    canReopenAsDraft: payload.can_reopen_as_draft === true,
    billingHoldReason: payload.billing_hold_reason || null,
    isNilReport: !!(payload.is_nil_report ?? payload.isNilReport),
    electronicInvoicing: normalizeElectronicInvoicingContext(payload.electronic_invoicing ?? payload.electronicInvoicing),
  };
};

function normalizeElectronicInvoicingContext(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const invoiceRaw = raw.invoice;
  const invoice =
    invoiceRaw && typeof invoiceRaw === 'object'
      ? {
          invoiceNumber: invoiceRaw.invoice_number || invoiceRaw.invoiceNumber || '',
          amountExclVat:
            invoiceRaw.amount_excl_vat != null
              ? Number(invoiceRaw.amount_excl_vat)
              : invoiceRaw.amountExclVat != null
                ? Number(invoiceRaw.amountExclVat)
                : 0,
          amountInclVat:
            invoiceRaw.amount_incl_vat != null
              ? Number(invoiceRaw.amount_incl_vat)
              : invoiceRaw.amountInclVat != null
                ? Number(invoiceRaw.amountInclVat)
                : 0,
          fileKey: invoiceRaw.file_key || invoiceRaw.fileKey || null,
          fileName: invoiceRaw.file_name || invoiceRaw.fileName || '',
          isReady: invoiceRaw.is_ready === true || invoiceRaw.isReady === true,
          emissionJobStatus: invoiceRaw.emission_job_status || invoiceRaw.emissionJobStatus || null,
        }
      : null;

  return {
    showElectronicEmission:
      raw.show_electronic_emission === true || raw.showElectronicEmission === true,
    requiresSelfInvoiceUpload:
      raw.requires_self_invoice_upload === true || raw.requiresSelfInvoiceUpload === true,
    electronicInvoicingMode:
      raw.electronic_invoicing_mode === true || raw.electronicInvoicingMode === true,
    invoice,
  };
}

export const activityReportService = {
  async getReports({ page = 1, pageSize = 10, search, status, submissionDate, submissionDateTo, missionId, reportMonth, sortBy, sortDirection, teamId, freelancerId, umbrella, isFavorite } = {}) {
    try {
      const params = {
        page,
        per_page: pageSize,
      };
      if (search) params.search = search;
      // Support array of statuses for multi-select filter
      if (status) {
        if (Array.isArray(status) && status.length > 0) {
          params.status = status.map(s => s.toUpperCase()).join(',');
        } else if (status !== 'all' && typeof status === 'string') {
          params.status = status.toUpperCase();
        }
      }
      // Only add mission_id if it's a valid UUID (not null, undefined, or 'null')
      if (missionId && missionId !== 'all' && missionId !== null && missionId !== undefined && missionId !== 'null' && missionId !== 'undefined') {
        // mission_id is a UUID, not a number
        params.mission_id = missionId;
      }
      if (reportMonth && /^\d{4}-\d{2}$/.test(String(reportMonth).trim())) {
        params.report_month = String(reportMonth).trim();
      }
      if (submissionDate) params.submission_date = submissionDate;
      if (submissionDateTo) params.submission_date_to = submissionDateTo;
      if (sortBy) params.sort_by = sortBy;
      if (sortDirection) params.sort_direction = sortDirection;
      // Ajouter team_id si fourni (pour les freelancers)
      if (teamId) {
        params.team_id = teamId;
      }
      // Ajouter freelancer_id si fourni (pour filtrer par freelancer spécifique)
      if (freelancerId) {
        params.freelancer_id = freelancerId;
      }
      if (umbrella) {
        params.umbrella = umbrella;
      }
      if (isFavorite) {
        params.is_favorite = 'true';
      }

      const response = await activityReportApi.fetchReports(params);
      const payload = response?.data || {};
      const items = Array.isArray(payload.items) ? payload.items : [];
      const list = items.map(normalizeReport);
      return {
        success: true,
        data: {
          list,
          total: payload.total ?? list.length,
          perPage: payload.per_page,
          currentPage: payload.current_page,
          accountingToolEmail: payload.accounting_tool_email || '',
        },
      };
    } catch (error) {
      console.error('activityReportService.getReports error', error);
      return {
        success: false,
        error: error?.response?.data?.message || error?.message || 'Failed to fetch activity reports',
        code: error?.response?.status ?? 'GET_REPORTS_ERROR',
      };
    }
  },

  async getReminderDates() {
    try {
      const res = await activityReportApi.fetchReminderDates();
      const payload = res?.data?.data ?? res?.data;
      const items = payload?.items ?? [];
      return { success: true, data: items };
    } catch (error) {
      console.error('activityReportService.getReminderDates error', error);
      return { success: false, data: [] };
    }
  },

  async reopenRejectedReport(activityReportId) {
    if (!activityReportId) return { success: false };
    try {
      const res = await activityReportApi.reopenRejectedReport(activityReportId);
      return { success: true, data: res?.data };
    } catch (error) {
      console.error('activityReportService.reopenRejectedReport error', error);
      return { success: false };
    }
  },

  /** Run invoice verification on uploaded PDF. body: { file_key, expected_amount_excl_vat? }. Returns { success, data } with invoice_verification_status, etc. */
  async runInvoiceVerification(activityReportId, body) {
    if (!activityReportId || !body?.file_key) return { success: false };
    try {
      const res = await activityReportApi.runInvoiceVerification(activityReportId, body);
      const data = res?.data?.data ?? res?.data;
      console.log('[activityReportService.runInvoiceVerification] raw res', { res, data, dataKeys: data ? Object.keys(data) : null });
      if (!data) {
        console.warn('[activityReportService.runInvoiceVerification] no data in response', res);
        return { success: false };
      }
      return { success: true, data };
    } catch (error) {
      console.error('activityReportService.runInvoiceVerification error', error);
      return { success: false };
    }
  },

  async getReportExport(activityReportId) {
    if (!activityReportId) return { success: false };
    try {
      const res = await activityReportApi.fetchReportExport(activityReportId);
      const normalized = normalizeReportExport(res?.data?.data || res?.data);
      return { success: true, data: normalized };
    } catch (error) {
      console.error('activityReportService.getReportExport error', error);
      return { success: false };
    }
  },

  async sendToAccounting(_id) {
    await simulateDelay(300);
    // Dans l'implémentation réelle, appeler https.post('/accounting/send', { id })
    return { success: true };
  },
  async togglePaidStatus({ activityReportId, paidType }) {
    if (!activityReportId || !paidType) return { success: false };
    try {
      const res = await activityReportApi.togglePaidStatus(activityReportId, paidType);
      return { success: true, data: res?.data };
    } catch (error) {
      console.error('activityReportService.togglePaidStatus error', error);
      return { success: false };
    }
  },
  async exportSepaXml(ids = []) {
    if (!ids.length) return { success: false };
    try {
      const res = await activityReportApi.exportSepaXml(ids, { rawResponse: true, responseType: 'blob' });
      return { success: true, data: res?.data, headers: res?.headers };
    } catch (error) {
      console.error('activityReportService.exportSepaXml error', error);
      return { success: false };
    }
  },
  async deleteReport(activityReportId) {
    if (!activityReportId) return { success: false };
    try {
      const res = await activityReportApi.deleteReport(activityReportId);
      return { success: true, data: res?.data };
    } catch (error) {
      console.error('activityReportService.deleteReport error', error);
      return { success: false };
    }
  },

  async createActivityReport({ missionId, missionType, reportMonth, timesheet, expenses, carbonFootprint, mission_feedback, invoicing, financialSummary }) {
    if (!missionId) return { success: false };
    try {
      const payload = {
        mission_id: missionId,
        report_month: reportMonth && /^\d{4}-\d{2}$/.test(String(reportMonth).trim()) ? String(reportMonth).trim() : undefined,
        timesheets: normalizeTimesheetsForApi(timesheet, missionType),
        expenses,
        transportation_mode: carbonFootprint?.transportationMode || null,
        one_way_distance_km: carbonOneWayDistanceKmToApi(carbonFootprint?.oneWayDistance),
        trips_per_week: carbonTripsPerWeekToApi(carbonFootprint?.tripsPerWeek),
        mission_feedback: mission_feedback ?? null,
        invoicing_options: buildInvoicingOptionsPayload(invoicing),
        invoice_amount_excl_vat: toNumberOrNull(financialSummary?.invoiceAmountExclVAT),
        expenses_amount_excl_vat: toNumberOrNull(financialSummary?.expensesAmountExclVAT),
        expenses_vat_total: toNumberOrNull(financialSummary?.expensesVatTotal),
      };
      const res = await activityReportApi.createReport(payload);
      return { success: true, data: res?.data };
    } catch (error) {
      console.error('activityReportService.createActivityReport error', error);
      return { success: false };
    }
  },

  async saveActivityReportDraft({ activityReportId, missionId, missionIds, missions, missionType, reportMonth, timesheet, expenses, carbonFootprint, mission_feedback, periodEconomicDependency, invoicing, financialSummary, supplementaryItems }) {
    if (!missionId || !activityReportId) {
      console.error('[activityReportService.saveActivityReportDraft] Missing required params:', { missionId, activityReportId });
      return { success: false, error: 'Missing required params (missionId, activityReportId)', code: 'MISSING_PARAMS' };
    }
    try {
      // Normalize expenses: convert receiptFile to receipt_url, receipt_key, and receipt_file_name
      const normalizedExpenses = Array.isArray(expenses) ? expenses.map((exp) => {
        const receiptFile = exp.receiptFile || exp.receipt_file;
        const amountInclVat = exp.amountInclVAT || exp.amount_incl_vat || null;
        const vatAmount = exp.vatAmount || exp.vat_amount || null;
        // amount_excl_vat est NOT NULL en base : on le dérive de l'incl - TVA si absent.
        let amountExclVat = exp.amountExclVAT || exp.amount_excl_vat || null;
        if (amountExclVat == null && amountInclVat != null) {
          const incl = parseFloat(amountInclVat) || 0;
          const vat = parseFloat(vatAmount) || 0;
          amountExclVat = (incl - vat).toFixed(2);
        }
        const missionId = exp.missionId ?? exp.mission_id ?? null;
        const normalized = {
          date: exp.date,
          type: exp.type,
          description: exp.description,
          mission_id: missionId,
          amount_excl_vat: amountExclVat,
          amount_incl_vat: amountInclVat,
          vat_rate: normalizeExpenseVatRateForApi({
            vatRate: exp.vatRate ?? exp.vat_rate,
            amountExclVat: amountExclVat,
            vatAmount: vatAmount,
          }),
          vat_amount: vatAmount,
          receipt_url: receiptFile?.url || receiptFile?.file_url || exp.receipt_url || null,
          receipt_key: receiptFile?.key || receiptFile?.file_key || exp.receipt_key || null,
          receipt_file_name: receiptFile?.name || receiptFile?.file_name || exp.receipt_file_name || exp.receiptFileName || null,
          billing_mode: exp.billingMode || exp.billing_mode || 'refacturation',
          vat_recoverable: (exp.vatRecoverable ?? exp.vat_recoverable) === true,
        };
        const reimbursement_percent = mapExpenseReimbursementPercentForApi(exp);
        if (reimbursement_percent != null) {
          normalized.reimbursement_percent = reimbursement_percent;
        }
        // Ensure all required fields are present
        if (!normalized.date || !normalized.type || !normalized.description) {
          console.warn('[saveActivityReportDraft] Expense missing required fields:', normalized);
        }
        return normalized;
      }) : [];
      
      // Transform timesheets to ensure description is present for monthly entries
      // Only add default description if description is null/undefined, not if it's an empty string (which could be a comment)
      const timesheetNormalizationOptions = {
        craMissionIds: missionIds,
        missions,
      };
      const transformedTimesheets = normalizeTimesheetsForApi(
        Array.isArray(timesheet)
          ? timesheet.map((ts) => {
              // For monthly entries, ensure description is present only if it's null/undefined
              // Empty string means user cleared the comment, so don't override it
              if (
                (ts.type === 'monthly' || ts.entryType === 'monthly') &&
                (ts.description === null || ts.description === undefined)
              ) {
                return {
                  ...ts,
                  description: 'J\'ai travaillé ce mois-ci', // Default description for monthly entries
                };
              }
              return ts;
            })
          : timesheet,
        missionType,
        timesheetNormalizationOptions
      );
      
      const payload = {
        mission_id: missionId,
        mission_ids: Array.isArray(missionIds) && missionIds.length > 0 ? missionIds : [missionId],
        report_month: reportMonth && /^\d{4}-\d{2}$/.test(String(reportMonth).trim()) ? String(reportMonth).trim() : undefined,
        timesheets: transformedTimesheets,
        expenses: normalizedExpenses,
        supplementary_items: normalizeSupplementaryItemsForApi(supplementaryItems),
        transportation_mode: carbonFootprint?.transportationMode || null,
        one_way_distance_km: carbonOneWayDistanceKmToApi(carbonFootprint?.oneWayDistance),
        trips_per_week: carbonTripsPerWeekToApi(carbonFootprint?.tripsPerWeek),
        mission_feedback: mission_feedback ?? null,
        period_economic_dependency: periodEconomicDependency ?? null,
        invoicing_options: buildInvoicingOptionsPayload(invoicing),
        invoice_amount_excl_vat: toNumberOrNull(financialSummary?.invoiceAmountExclVAT),
        expenses_amount_excl_vat: toNumberOrNull(financialSummary?.expensesAmountExclVAT),
        expenses_vat_total: toNumberOrNull(financialSummary?.expensesVatTotal),
      };
      const res = await activityReportApi.saveDraft(activityReportId, payload);
      return { success: true, data: res?.data };
    } catch (error) {
      console.error('[activityReportService.saveActivityReportDraft] Error:', error);
      return {
        success: false,
        error: error?.response?.data?.message || error?.message || 'Failed to save activity report draft',
        code: error?.response?.status ?? 'SAVE_DRAFT_ERROR',
      };
    }
  },

  async submitActivityReport({ activityReportId, missionId, missionIds, missions, missionType, timesheet, expenses, carbonFootprint, mission_feedback, periodEconomicDependency, invoicing, financialSummary, supplementaryItems, isNilReport = false }) {
    if (!missionId || !activityReportId) {
      console.error('[activityReportService.submitActivityReport] Missing required params:', { missionId, activityReportId });
      return { success: false, error: 'Missing required params (missionId, activityReportId)', code: 'MISSING_PARAMS' };
    }
    try {
      // Normalize expenses: convert camelCase/receiptFile to snake_case (same as saveActivityReportDraft)
      // Backend submit expects amount_excl_vat (NOT NULL), receipt_url, receipt_key, receipt_file_name
      const normalizedExpenses = Array.isArray(expenses) ? expenses.map((exp) => {
        const receiptFile = exp.receiptFile || exp.receipt_file;
        const amountInclVat = exp.amountInclVAT ?? exp.amount_incl_vat ?? null;
        const vatAmount = exp.vatAmount ?? exp.vat_amount ?? null;
        // amount_excl_vat est NOT NULL en base : on le dérive de l'incl - TVA si absent
        // (aligné sur saveActivityReportDraft pour éviter une divergence HT brouillon vs soumission).
        let amountExcl = exp.amountExclVAT ?? exp.amount_excl_vat ?? null;
        if ((amountExcl == null || amountExcl === '') && amountInclVat != null) {
          const incl = parseFloat(amountInclVat) || 0;
          const vat = parseFloat(vatAmount) || 0;
          amountExcl = incl - vat;
        }
        const amountExclNum = amountExcl != null && amountExcl !== '' ? parseFloat(amountExcl) : 0;
        const missionId = exp.missionId ?? exp.mission_id ?? null;
        const row = {
          date: exp.date,
          type: exp.type,
          description: exp.description ?? '',
          mission_id: missionId,
          amount_excl_vat: Number.isFinite(amountExclNum) ? amountExclNum : 0,
          amount_incl_vat: amountInclVat,
          vat_rate: normalizeExpenseVatRateForApi({
            vatRate: exp.vatRate ?? exp.vat_rate,
            amountExclVat: amountExclNum,
            vatAmount: vatAmount,
          }),
          vat_amount: vatAmount,
          receipt_url: receiptFile?.url ?? receiptFile?.file_url ?? exp.receipt_url ?? null,
          receipt_key: receiptFile?.key ?? receiptFile?.file_key ?? exp.receipt_key ?? null,
          receipt_file_name: receiptFile?.name ?? receiptFile?.file_name ?? exp.receipt_file_name ?? exp.receiptFileName ?? null,
          billing_mode: exp.billingMode ?? exp.billing_mode ?? 'refacturation',
          vat_recoverable: (exp.vatRecoverable ?? exp.vat_recoverable) === true,
        };
        const reimbursement_percent = mapExpenseReimbursementPercentForApi(exp);
        if (reimbursement_percent != null) {
          row.reimbursement_percent = reimbursement_percent;
        }
        return row;
      }) : [];

      // Transform timesheets to ensure description is present for monthly entries
      // Only add default description if description is null/undefined, not if it's an empty string (which could be a comment)
      const timesheetNormalizationOptions = {
        craMissionIds: missionIds,
        missions,
      };
      const transformedTimesheets = normalizeTimesheetsForApi(
        Array.isArray(timesheet)
          ? timesheet.map((ts) => {
              if (
                (ts.type === 'monthly' || ts.entryType === 'monthly') &&
                (ts.description === null || ts.description === undefined)
              ) {
                return {
                  ...ts,
                  description: 'J\'ai travaillé ce mois-ci',
                };
              }
              return ts;
            })
          : timesheet,
        missionType,
        timesheetNormalizationOptions
      );
      
      // CRA néant : on envoie le flag explicite. Le backend ignore alors temps/frais/montants
      // et auto-valide le rapport (APPROVED) sans écriture comptable ni notification.
      const nilReport = !!isNilReport;
      const payload = {
        mission_id: missionId,
        mission_ids: Array.isArray(missionIds) && missionIds.length > 0 ? missionIds : [missionId],
        is_nil_report: nilReport,
        timesheets: nilReport ? [] : transformedTimesheets,
        expenses: nilReport ? [] : normalizedExpenses,
        supplementary_items: nilReport ? [] : normalizeSupplementaryItemsForApi(supplementaryItems),
        transportation_mode: carbonFootprint?.transportationMode || null,
        one_way_distance_km: carbonOneWayDistanceKmToApi(carbonFootprint?.oneWayDistance),
        trips_per_week: carbonTripsPerWeekToApi(carbonFootprint?.tripsPerWeek),
        mission_feedback: mission_feedback ?? null,
        period_economic_dependency: nilReport ? null : (periodEconomicDependency ?? null),
        invoicing_options: nilReport ? null : buildInvoicingOptionsPayload(invoicing),
        invoice_amount_excl_vat: nilReport ? 0 : toNumberOrNull(financialSummary?.invoiceAmountExclVAT),
        expenses_amount_excl_vat: nilReport ? 0 : toNumberOrNull(financialSummary?.expensesAmountExclVAT),
        expenses_vat_total: nilReport ? 0 : toNumberOrNull(financialSummary?.expensesVatTotal),
      };
      
      const res = await activityReportApi.submitReport(activityReportId, payload);
      const submitted = res?.data ?? null;

      return { success: true, data: submitted };
    } catch (error) {
      console.error('[activityReportService.submitActivityReport] Error:', {
        error,
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
      });
      const responseData = error?.response?.data;
      return {
        success: false,
        error: responseData?.message || error?.message || 'Unknown error',
        code: error?.response?.status ?? 'SUBMIT_ERROR',
        errorCode: responseData?.data?.error_code ?? responseData?.error_code ?? null,
      };
    }
  },

  async reviewActivityReport({ activityReportId, action, comment }) {
    if (!activityReportId || !action) return { success: false };
    try {
      const payload = {
        action,
        comment: comment ?? null,
      };
      const res = await activityReportApi.reviewReport(activityReportId, payload);
      return { success: true, data: res?.data };
    } catch (error) {
      console.error('activityReportService.reviewActivityReport error', error);
      return { success: false };
    }
  },

  async resendClientValidationEmail(activityReportId) {
    if (!activityReportId) return { success: false };
    try {
      await activityReportApi.resendClientValidationEmail(activityReportId);
      return { success: true };
    } catch (error) {
      console.error('activityReportService.resendClientValidationEmail error', error);
      return { success: false, error: error?.response?.data?.message || error?.message };
    }
  },

  async getMissionOptions({ teamId, excludeReportId } = {}) {
    try {
      const params = {};
      // SECURITY: Always pass teamId for freelancers to filter by team
      if (teamId) {
        params.team_id = teamId;
      }
      if (excludeReportId) {
        params.exclude_report_id = excludeReportId;
      }
      const res = await activityReportApi.fetchMissionOptions(params);
      const items = res?.data?.items || [];
      return { success: true, data: { items } };
    } catch (error) {
      console.error('activityReportService.getMissionOptions error', error);
      return { success: false, data: { items: [] } };
    }
  },

  async getMissionList({ teamId } = {}) {
    try {
      const params = {};
      // SECURITY: Always pass teamId for freelancers to filter by team
      if (teamId) {
        params.team_id = teamId;
      }
      const res = await activityReportApi.fetchMissionList(params);
      const items = res?.data?.items || [];
      return { success: true, data: { items } };
    } catch (error) {
      console.error('activityReportService.getMissionList error', error);
      return { success: false, data: { items: [] } };
    }
  },

  /** Périodes à déclarer (todo) côté backend : une requête, pas d’empty state avant la liste */
  async getAvailablePeriods({ teamId, excludeReportId } = {}) {
    try {
      const params = {};
      if (teamId) params.team_id = teamId;
      if (excludeReportId) params.exclude_report_id = excludeReportId;
      const res = await activityReportApi.fetchAvailablePeriods(params);
      const items = res?.data?.items ?? [];
      return { success: true, data: { items } };
    } catch (error) {
      console.error('activityReportService.getAvailablePeriods error', error);
      return { success: false, data: { items: [] } };
    }
  },

  async initializeActivityReport(missionIds, reportMonthOrPeriodLabel, periodLabelOnly) {
    const normalizedMissionIds = Array.isArray(missionIds) ? missionIds : [missionIds];
    if (!normalizedMissionIds.filter(Boolean).length || (!reportMonthOrPeriodLabel && !periodLabelOnly)) return { success: false };
    try {
      const res = await activityReportApi.initializeActivityReport(normalizedMissionIds, reportMonthOrPeriodLabel, periodLabelOnly);
      return { success: true, data: res?.data };
    } catch (error) {
      console.error('activityReportService.initializeActivityReport error', error);
      return { success: false };
    }
  },

  async getReportDetail(activityReportId) {
    if (!activityReportId) {
      console.error('[getReportDetail] No activityReportId provided');
      return { success: false, error: 'No activityReportId provided', code: 'MISSING_PARAMS' };
    }
    
    // Normalize the ID: remove suffix like '-expenses' but keep full UUID
    // UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx (36 chars with dashes)
    let normalizedId = activityReportId;
    if (typeof activityReportId === 'string') {
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const knownSuffixes = ['-expenses', '-invoice'];
      
      // If it's a valid UUID, return as is
      if (uuidPattern.test(activityReportId)) {
        normalizedId = activityReportId;
      } else {
        // If it ends with a known suffix, remove it
        let found = false;
        for (const suffix of knownSuffixes) {
          if (activityReportId.endsWith(suffix)) {
            normalizedId = activityReportId.slice(0, -suffix.length);
            found = true;
            break;
          }
        }
        // Otherwise, return as is
        if (!found) {
          normalizedId = activityReportId;
        }
      }
    }
    
    try {
      // fetchReportDetail uses get() which returns the HTTP body (axios res.data), not the full axios response
      const body = await activityReportApi.fetchReportDetail(normalizedId);
      // API returns { code, message, data: { data: responseData } } or { code, message, data: responseData }
      const inner = body?.data;
      const payload = (inner && typeof inner === 'object' && 'data' in inner && inner.data != null)
        ? inner.data
        : inner;
      
      if (!payload || typeof payload !== 'object') {
        return { success: false, error: 'No payload in response', code: 'EMPTY_PAYLOAD' };
      }
      
      const normalized = normalizeReportDetail(payload);
      
      if (!normalized) {
        return { success: false, error: 'Normalization returned null', code: 'NORMALIZATION_FAILED' };
      }
      return { success: true, data: normalized };
    } catch (error) {
      console.error('[getReportDetail] Exception caught:', {
        error,
        errorMessage: error?.message,
        errorStack: error?.stack,
        originalId: activityReportId,
        normalizedId,
        errorResponse: error?.response?.data,
        errorStatus: error?.response?.status,
      });
      return {
        success: false,
        error: error?.response?.data?.message || error?.message || 'Unknown error',
        code: error?.response?.status ?? 'GET_REPORT_DETAIL_ERROR',
      };
    }
  },
};

export default activityReportService;
